import {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router';

import { useScreenSizes } from '@hooks/useScreenSizes';
import { useDownloadAppModal } from '@hooks/useDownloadAppModal';
import { useScroll } from '@hooks/useScroll';
import {
  useLazyGetUserDataByIdQuery,
  useLazyGetUserRecordedShowsQuery,
} from '@redux/queries/user';
import {
  useRecordedShows,
  useRecordedShowsMayLoadMore,
} from '@redux/selectors/shows';
import { useAuthUser } from '@redux/selectors/auth';
import { useUserDataById } from '@redux/selectors/users';
import { ProfileTab } from '@utils/typings/user';
import RecordedShowCard from '@components/show/card/RecordedShowCard';
import { DefaultText } from '@components/text/Text';
import Loader from '@components/loader';
import ProfileInfo from '@components/profileInfo';
import SkewedButton from '@components/buttons/SkewedButton';
import ProfileButtons from '@components/profileInfo/ProfileButtons';
import UserProfileButtons from '@components/profileInfo/UserProfileButtons';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: column;
    align-items: center;
    gap: 16px;
    overflow-y: auto;
    ${({ theme }) => theme.scrollbar.thin}
  }
`;

const TabsContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  min-height: 0;
`;

const TabsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  padding-inline: 16px;
  gap: 16px;
`;

const Tab = styled(SkewedButton)`
  width: 100%;
  max-width: 400px;
  min-width: 120px;
`;

const ListContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 16px;
  padding: 16px;
  padding-bottom: 48px;
  ${({ theme }) => theme.scrollbar.default}
  overflow-y: auto;
  min-height: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 12px;
    overflow-y: unset;
    padding-bottom: 32px;
  }
`;

const EmptyContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const EmptyState = styled(DefaultText)`
  width: 100%;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[400]};
`;

const Profile: FC = () => {
  const [activeTab, setActiveTab] = useState<ProfileTab>(
    ProfileTab.RecordedShows,
  );
  const desktopContainerRef = useRef<HTMLDivElement>(null);
  const tabletsContainerRef = useRef<HTMLDivElement>(null);
  const { id } = useParams();
  const { user: currentUser } = useAuthUser();
  const user = useUserDataById(id);
  const isMe = useMemo(
    () => !!currentUser?.id && currentUser?.id === user?.id,
    [currentUser?.id, user?.id],
  );
  const recordedShowsIds = useRecordedShows(user?.id || '');
  const recordedShowsMayLoadMore = useRecordedShowsMayLoadMore(user?.id || '');
  const [getUserData, { isFetching, isError }] = useLazyGetUserDataByIdQuery();
  const [getUserRecordedShows] = useLazyGetUserRecordedShowsQuery();
  const { isTablet, isMobile } = useScreenSizes();
  const { openDownloadAppModal } = useDownloadAppModal();

  const handleActiveTab = useCallback(
    (tab: ProfileTab) => {
      if (tab === ProfileTab.Channels) {
        openDownloadAppModal();
        return;
      }
      setActiveTab(tab);
    },
    [setActiveTab, openDownloadAppModal],
  );

  const renderActions = useCallback(() => {
    if (!user?.id) {
      return null;
    }

    return isMe ? (
      <ProfileButtons userId={user?.id} />
    ) : (
      <UserProfileButtons userId={user?.id} />
    );
  }, [user?.id, isMe]);

  const renderScene = useCallback(
    (tab: ProfileTab) => {
      switch (tab) {
        case ProfileTab.RecordedShows:
          return (
            <ListContainer
              ref={isTablet || isMobile ? undefined : desktopContainerRef}
            >
              {!recordedShowsIds.length ? (
                <EmptyContainer>
                  <DefaultText>{'No shows yet...'}</DefaultText>
                </EmptyContainer>
              ) : (
                recordedShowsIds.map((id: string) => (
                  <RecordedShowCard key={id} showId={id} />
                ))
              )}

              {!!recordedShowsIds.length && isFetching && (
                <Loader size={'large'} />
              )}
            </ListContainer>
          );
        default:
          break;
      }
    },
    [recordedShowsIds, isFetching, isTablet, isMobile],
  );

  const getTabContent = useCallback(
    (isRefreshState: boolean, lastId?: string) => {
      switch (activeTab) {
        case ProfileTab.RecordedShows:
          if (user?.id) {
            getUserRecordedShows({
              userId: user?.id,
              lastId,
              isMe,
              isRefresh: isRefreshState,
            });
          }
          break;
        default:
          break;
      }
    },
    [activeTab, user?.id, isMe, getUserRecordedShows],
  );

  const onLoadMore = useCallback(() => {
    switch (activeTab) {
      case ProfileTab.RecordedShows:
        if (recordedShowsMayLoadMore) {
          const lastId = recordedShowsIds[recordedShowsIds.length - 1];
          getTabContent(false, lastId);
        }
        break;
      default:
        break;
    }
  }, [activeTab, recordedShowsMayLoadMore, recordedShowsIds, getTabContent]);

  useScroll({
    onLoadMore,
    containerRef:
      isTablet || isMobile ? tabletsContainerRef : desktopContainerRef,
  });

  useEffect(() => {
    if (user?.id) {
      getUserData({ id: user?.id });
    }
  }, [getUserData, user?.id]);

  useEffect(() => {
    getTabContent(true);
  }, [getTabContent]);

  if (!user?.id || isError) {
    return <EmptyState>{'User not found'}</EmptyState>;
  }

  return (
    <Container ref={isTablet || isMobile ? tabletsContainerRef : undefined}>
      <ProfileInfo userId={user?.id || ''} renderActions={renderActions} />

      <TabsContainer>
        <TabsWrapper>
          {Object.values(ProfileTab).map((tab) => (
            <Tab
              key={tab}
              onClick={() => handleActiveTab(tab)}
              isGradient={activeTab === tab}
            >
              {tab}
            </Tab>
          ))}
        </TabsWrapper>

        {renderScene(activeTab)}
      </TabsContainer>
    </Container>
  );
};

export default memo(Profile);
